/**
 * HypothesesSection - Right section showing related hypotheses to validate
 * Question: "What do we think will solve this? What should we test?"
 */

import React from 'react';
import { Lightbulb, Target, FlaskConical, CheckCircle2 } from 'lucide-react';
import { RelatedItemCard, NODE_COLORS } from './RelatedItemCard';
import type { RelatedHypothesis } from './hooks/useIssueRelationships';

interface HypothesesSectionProps {
  hypotheses: RelatedHypothesis[];
  onHypothesisClick?: (hypothesisId: string) => void;
}

// Hypothesis type display
const HYPOTHESIS_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  root_cause: { label: 'Root Cause', color: 'bg-red-100 text-red-700' },
  performance: { label: 'Performance', color: 'bg-blue-100 text-blue-700' },
  process_efficiency: { label: 'Process', color: 'bg-green-100 text-green-700' },
  cost_optimization: { label: 'Cost', color: 'bg-purple-100 text-purple-700' },
  risk_mitigation: { label: 'Risk', color: 'bg-orange-100 text-orange-700' },
  growth_opportunity: { label: 'Growth', color: 'bg-emerald-100 text-emerald-700' },
};

export const HypothesesSection: React.FC<HypothesesSectionProps> = ({
  hypotheses,
  onHypothesisClick,
}) => {
  const colors = NODE_COLORS.Hypothesis;

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden h-full`}>
      {/* Header */}
      <div className="px-4 py-3 bg-white/60 border-b border-amber-100">
        <div className="flex items-center gap-2">
          <Lightbulb className={`w-5 h-5 ${colors.accent}`} />
          <h3 className={`font-semibold ${colors.accent}`}>Validate: Hypotheses</h3>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          What we believe will work
        </p>
      </div>

      {/* Content */}
      <div className="p-4 overflow-y-auto max-h-[400px]">
        {hypotheses.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {hypotheses.map((item) => (
              <RelatedItemCard
                key={item.hypothesis.hypothesis_id}
                title={item.hypothesis.title}
                description={item.hypothesis.statement}
                type="Hypothesis"
                relationship={item.relationship}
                accentColor={colors.accent}
                borderColor={colors.border}
                bgColor="bg-white"
              >
                <div className="space-y-2">
                  {/* Hypothesis Type */}
                  {item.hypothesis.hypothesis_type && (
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded ${
                        HYPOTHESIS_TYPE_LABELS[item.hypothesis.hypothesis_type]?.color ||
                        'bg-neutral-100 text-neutral-600'
                      }`}
                    >
                      {HYPOTHESIS_TYPE_LABELS[item.hypothesis.hypothesis_type]?.label ||
                        item.hypothesis.hypothesis_type}
                    </span>
                  )}

                  {/* Rationale */}
                  {item.hypothesis.rationale && (
                    <div className="flex items-start gap-2">
                      <Target className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-neutral-600 line-clamp-2">
                        {item.hypothesis.rationale}
                      </span>
                    </div>
                  )}

                  {/* Test Approach */}
                  {item.hypothesis.test_approach && (
                    <div className="flex items-start gap-2">
                      <FlaskConical className="w-3 h-3 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-neutral-600 line-clamp-2">
                        <span className="font-medium">Test:</span> {item.hypothesis.test_approach}
                      </span>
                    </div>
                  )}

                  {/* Validation Stats */}
                  {(item.hypothesis.validation_count > 0 || item.hypothesis.total_tests > 0) && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-neutral-600">
                        Validated: {item.hypothesis.validation_count}/{item.hypothesis.total_tests}
                      </span>
                    </div>
                  )}
                </div>
              </RelatedItemCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="text-center py-6">
    <Lightbulb className="w-10 h-10 text-amber-200 mx-auto mb-2" />
    <p className="text-sm text-neutral-500">No related hypotheses linked</p>
    <p className="text-xs text-neutral-400 mt-1">
      Hypotheses help validate potential solutions
    </p>
  </div>
);

export default HypothesesSection;
